/**
 * Availability band -> label and action (spec §3.6). The band comes from app.kgob.com through the backend and
 * may be up to 5 minutes old (or, in the postbuild snapshot, hours old), so the time-driven states are
 * re-derived from the clock: at render time and again every minute in the browser (K-7). Never a number:
 * the API publishes bands only.
 */
import { fmtDay } from "./time";

/** Started, not finished. Their window fix means such a date now reaches us instead of vanishing. */
export const isInProgress = (s, now = new Date()) => new Date(s.starts_at) <= now && new Date(s.ends_at) >= now;

export function sessionState(s, event, now = new Date()) {
  if (event.status === "cancelled" || s.status === "cancelled") return "cancelled";
  if (new Date(s.ends_at) < now) return "ended";
  if (s.status === "postponed") return "postponed";
  if (s.status === "rescheduled") return "rescheduled";
  if (s.status === "moved_online") return "moved_online";
  return "scheduled";
}

/** Recomputes the time-driven bands so a stale page never says RSVP for a closed date. */
export function effectiveAvailability(s, event, now = new Date()) {
  const state = sessionState(s, event, now);
  if (state === "cancelled" || state === "ended" || state === "postponed") return "closed";
  if (s.rsvp_opens_at && new Date(s.rsvp_opens_at) > now) return "not_open";
  if (s.rsvp_closes_at && new Date(s.rsvp_closes_at) <= now) return "closed";
  if (s.availability === "not_open") return "open"; // opened since the last refresh
  return s.availability;
}

/** The availability badge for a date (§3.6 wording; the meaning is never carried by colour alone). */
export function badgeFor(s, event, now = new Date()) {
  const state = sessionState(s, event, now);
  if (state === "cancelled") return { label: "Cancelled", tone: "cancelled", glyph: "x" };
  if (state === "ended") return { label: "Ended", tone: "grey", glyph: "check" };
  if (state === "postponed") return { label: "Postponed", tone: "grey", glyph: "pause" };
  // An event under way reads as "Happening now"; the date alone looks like it has already passed. Their band is
  // `open` for a running date because the firm can still take a walk-in, so the RSVP button is left to the band.
  if (isInProgress(s, now)) return { label: "Happening now", tone: "open", glyph: "dot" };
  switch (effectiveAvailability(s, event, now)) {
    case "open": return { label: "Open", tone: "open", glyph: "dot" };
    case "limited": return { label: "Few spots left", tone: "limited", glyph: "half" };
    case "waitlist": return { label: "Full: waitlist open", tone: "waitlist", glyph: "list" };
    case "full": return { label: "Full", tone: "grey", glyph: "ring" };
    case "not_open": return { label: `Opens ${s.rsvp_opens_at ? fmtDay(s.rsvp_opens_at, s.timezone) : "soon"}`, tone: "grey", glyph: "ring" };
    case "closed":
    default: return { label: "Closed", tone: "grey", glyph: "ring" };
  }
}

export function actionFor(s, event, now = new Date()) {
  switch (effectiveAvailability(s, event, now)) {
    case "open":
    case "limited":
      return { kind: "rsvp", label: "RSVP" };
    case "waitlist":
      return { kind: "rsvp", label: "Join the waitlist" };
    default:
      return { kind: "none" };
  }
}

export const isBookable = (s, event, now = new Date()) => actionFor(s, event, now).kind === "rsvp";

/** Dates that have not ended, soonest first (§3.7: ended dates are hidden at render time). */
export const upcomingSessions = (event, now = new Date()) =>
  (event.event_sessions || []).filter((s) => new Date(s.ends_at) >= now).sort((a, b) => a.starts_at.localeCompare(b.starts_at));

/** The date a slide shows: the next bookable one, else the next upcoming one, else null. */
export function nextDate(event, now = new Date()) {
  const up = upcomingSessions(event, now);
  return (up || []).find((s) => isBookable(s, event, now)) ?? (up || []).find((s) => sessionState(s, event, now) !== "cancelled") ?? up[0] ?? null;
}
